import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAlpaca } from '../../context/AlpacaContext';
import { useDemo } from '../../context/DemoContext';

/**
 * Wraps protected routes to redirect users who haven't connected Alpaca
 * to /onboarding. No skip/dismiss — users must connect a brokerage account
 * to access the app.
 *
 * Skips the redirect if:
 * - User is not authenticated (let ProtectedRoute handle that)
 * - Alpaca status is still loading
 * - User has already connected Alpaca
 * - Already on /onboarding or /auth/alpaca/callback
 */
const OnboardingGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isConnected, isLoading } = useAlpaca();
  const { isDemoMode } = useDemo();
  const location = useLocation();

  // Don't guard if not authenticated — ProtectedRoute handles that
  if (!user) {
    return <>{children}</>;
  }

  // Wait for Alpaca status to load
  if (isLoading) {
    return <>{children}</>;
  }

  // Admin users bypass onboarding — they use shared env var credentials
  if (user.role === 'admin') {
    return <>{children}</>;
  }

  // Demo mode — show fake data, no Alpaca connection needed
  if (isDemoMode) {
    return <>{children}</>;
  }

  // Already connected — no need to redirect
  if (isConnected) {
    return <>{children}</>;
  }

  // Don't redirect if already on onboarding or callback pages
  const exemptPaths = ['/onboarding', '/auth/alpaca/callback'];
  if (exemptPaths.some((p) => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }

  // Redirect to onboarding — must connect Alpaca to use the app
  return <Navigate to="/onboarding" state={{ from: location }} replace />;
};

export default OnboardingGuard;
